import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Typography,
    Box,
    IconButton,
    CircularProgress,
    TextField,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { Company, Application, CompanyApplication } from '../../models/Company'
import { axiosInstance } from '../../helpers'
import {
    REQUIRED_APPLICATION_SLUGS,
    APPLICATION_SLUGS,
    ApplicationSlug,
} from '../../constants/applications'
import { Theme } from '@material-ui/core/styles/createMuiTheme'

/** Min width so price boxes are consistent (e.g. "$0.00/mo" vs "$19,300.00/mo"). */
const PRICE_BOX_MIN_WIDTH = 130

/** Renders total price (emphasized) and per-unit price. Total is never "Free" (shows $0.00/mo when 0 units). */
const PriceDisplay = ({
    total,
    perUnit,
    theme,
}: {
    total: string
    perUnit: string
    theme: Theme
}) => {
    const isZeroTotal = total.startsWith('$0.00')
    return (
        <Box
            style={{
                minWidth: PRICE_BOX_MIN_WIDTH,
                padding: theme.spacing(1, 1.5),
                borderRadius: 8,
                backgroundColor: isZeroTotal
                    ? theme.palette.grey?.[200] || theme.palette.action.selected
                    : theme.palette.primary.main,
                color: isZeroTotal
                    ? theme.palette.text.secondary
                    : theme.palette.primary.contrastText,
                textAlign: 'right',
                boxSizing: 'border-box',
            }}
        >
            <Typography
                variant="body2"
                style={{
                    fontWeight: 700,
                    lineHeight: 1.3,
                }}
            >
                {total}
            </Typography>
            <Typography
                variant="caption"
                style={{
                    opacity: isZeroTotal ? 1 : 0.9,
                    lineHeight: 1.3,
                }}
            >
                {perUnit}
            </Typography>
        </Box>
    )
}

/** Format price per unit for display (e.g. "99.00" → "$99.00/unit/mo", 0 → "Free"). */
function formatPerUnitPrice(priceString: string): string {
    const n = parseFloat(priceString)
    if (Number.isNaN(n) || n === 0) return 'Free'
    return `$${n.toFixed(2)}/unit/mo`
}

/** Format total price for display. Never "Free" — use $0.00/mo when 0 (e.g. 0 units). */
function formatTotalPrice(total: number): string {
    return `$${total.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}/mo`
}

/** Get raw price per unit (number) for an application. For EZTurn, falls back to company.billable_unit_rate when no app-level price exists. */
function getPricePerUnitValue(
    application: Application,
    companyApp: CompanyApplication | undefined,
    company: Company | null,
): number {
    const raw = companyApp?.effective_price ?? application.default_price ?? '0'
    const n = parseFloat(raw)
    if (
        n === 0 &&
        application.slug.toLowerCase() === APPLICATION_SLUGS.EZTURN &&
        company?.billable_unit_rate
    ) {
        const fallback = parseFloat(company.billable_unit_rate)
        if (!Number.isNaN(fallback)) return fallback
    }
    return Number.isNaN(n) ? 0 : n
}

/** Get per-unit price string and total price for display. */
function getPriceDisplay(
    application: Application,
    companyApp: CompanyApplication | undefined,
    unitCount: number,
    company: Company | null,
): { perUnit: string; total: string; totalValue: number } {
    const perUnitValue = getPricePerUnitValue(application, companyApp, company)
    const totalValue = perUnitValue * unitCount
    const perUnit = formatPerUnitPrice(String(perUnitValue))
    const total = formatTotalPrice(totalValue)
    return { perUnit, total, totalValue }
}

interface CompanyApplicationsModalProps {
    open: boolean
    onClose: () => void
    company: Company | null
    onSave: () => void
}

export const CompanyApplicationsModal: React.FC<CompanyApplicationsModalProps> = ({
    open,
    onClose,
    company,
    onSave,
}) => {
    const theme = useTheme()
    const [availableApplications, setAvailableApplications] = useState<
        Application[]
    >([])
    const [selectedApplicationIds, setSelectedApplicationIds] = useState<
        Set<number>
    >(new Set())
    const [loading, setLoading] = useState(false)
    const [fetchingApplications, setFetchingApplications] = useState(false)
    const [search, setSearch] = useState('')

    // IDs of applications that must always be selected (e.g. EZFolio)
    const requiredApplicationIds = React.useMemo(
        () =>
            new Set(
                availableApplications
                    .filter((app) =>
                        REQUIRED_APPLICATION_SLUGS.includes(
                            app.slug.toLowerCase() as ApplicationSlug,
                        ),
                    )
                    .map((app) => app.id),
            ),
        [availableApplications],
    )

    // Sum of (price per unit × company units) for each selected application
    const selectedTotalMonthly = React.useMemo(() => {
        const unitCount = company?.billable_unit_count ?? 0
        let sum = 0
        selectedApplicationIds.forEach((id) => {
            const app = availableApplications.find((a) => a.id === id)
            if (!app) return
            const companyApp = company?.applications?.find((a) => a.id === id)
            sum += getPricePerUnitValue(app, companyApp, company) * unitCount
        })
        return sum
    }, [company, selectedApplicationIds, availableApplications])

    // Fetch available applications when modal opens; merge required app IDs into selection
    useEffect(() => {
        if (open) {
            fetchAvailableApplications()
            if (company?.applications) {
                setSelectedApplicationIds(
                    new Set(company.applications.map((app) => app.id)),
                )
            } else {
                setSelectedApplicationIds(new Set())
            }
            setSearch('')
        }
    }, [open, company])

    // When available applications load, ensure required IDs are always in selection
    useEffect(() => {
        if (open && requiredApplicationIds.size > 0) {
            setSelectedApplicationIds((prev) => {
                const next = new Set(prev)
                requiredApplicationIds.forEach((id) => next.add(id))
                return next
            })
        }
    }, [open, requiredApplicationIds])

    const fetchAvailableApplications = async () => {
        setFetchingApplications(true)
        try {
            const response = await axiosInstance.get(
                '/beta/api/company/applications/',
                {
                    params: {
                        active_only: true,
                    },
                },
            )
            setAvailableApplications(response.data)
        } catch (error: any) {
            console.error('Error fetching applications:', error)
            toast.error('Failed to load applications')
        } finally {
            setFetchingApplications(false)
        }
    }

    const handleToggleApplication = (applicationId: number) => {
        if (requiredApplicationIds.has(applicationId)) return
        setSelectedApplicationIds((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(applicationId)) {
                newSet.delete(applicationId)
            } else {
                newSet.add(applicationId)
            }
            return newSet
        })
    }

    const handleSave = async () => {
        if (!company) return

        setLoading(true)
        try {
            const currentApplicationIds = new Set(
                company.applications?.map((app) => app.id) || [],
            )

            // Applications to grant (selected but not currently granted)
            const toGrant = Array.from(selectedApplicationIds).filter(
                (id) => !currentApplicationIds.has(id),
            )

            // Applications to revoke (currently granted but not selected); never revoke required apps
            const requiredIds = new Set(
                availableApplications
                    .filter((app) =>
                        REQUIRED_APPLICATION_SLUGS.includes(
                            app.slug.toLowerCase() as ApplicationSlug,
                        ),
                    )
                    .map((app) => app.id),
            )
            const toRevoke = Array.from(currentApplicationIds).filter(
                (id) => !selectedApplicationIds.has(id) && !requiredIds.has(id),
            )

            // Grant new applications
            for (const applicationId of toGrant) {
                try {
                    await axiosInstance.post(
                        `/beta/api/company/${company.id}/applications/${applicationId}/`,
                    )
                } catch (error: any) {
                    console.error(
                        `Error granting application ${applicationId}:`,
                        error,
                    )
                    toast.error(
                        `Failed to grant access to ${
                            availableApplications.find(
                                (app) => app.id === applicationId,
                            )?.name || 'application'
                        }`,
                    )
                }
            }

            // Revoke removed applications
            for (const applicationId of toRevoke) {
                try {
                    await axiosInstance.delete(
                        `/beta/api/company/${company.id}/applications/${applicationId}/`,
                    )
                } catch (error: any) {
                    console.error(
                        `Error revoking application ${applicationId}:`,
                        error,
                    )
                    toast.error(
                        `Failed to revoke access from ${
                            availableApplications.find(
                                (app) => app.id === applicationId,
                            )?.name || 'application'
                        }`,
                    )
                }
            }

            if (toGrant.length > 0 || toRevoke.length > 0) {
                toast.success('Application access updated successfully')
                onSave()
                onClose()
            } else {
                onClose()
            }
        } catch (error) {
            console.error('Error saving application access:', error)
            toast.error('Failed to save changes')
        } finally {
            setLoading(false)
        }
    }

    const filteredApplications = availableApplications.filter((app) =>
        app.name.toLowerCase().includes(search.toLowerCase()),
    )

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: theme.shape.borderRadius * 3,
                    boxShadow: theme.shadows[10],
                },
            }}
        >
            <DialogTitle
                style={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: theme.palette.primary.contrastText,
                    padding: theme.spacing(3),
                    textAlign: 'center',
                    position: 'relative',
                }}
            >
                <Typography
                    variant="h5"
                    component="div"
                    style={{ fontWeight: 600 }}
                >
                    Manage Application Access
                </Typography>
                <IconButton
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        right: theme.spacing(1),
                        top: theme.spacing(1),
                        color: theme.palette.primary.contrastText,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent style={{ padding: theme.spacing(3) }}>
                {company && (
                    <Box style={{ marginBottom: theme.spacing(2) }}>
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            {company.name}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: theme.spacing(0.5) }}
                        >
                            Select the applications this company has access to
                        </Typography>
                    </Box>
                )}

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search applications..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ marginBottom: theme.spacing(2) }}
                    InputProps={{
                        style: {
                            borderRadius: theme.shape.borderRadius,
                        },
                    }}
                />

                {fetchingApplications ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        style={{ padding: theme.spacing(4) }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        style={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: theme.shape.borderRadius,
                            maxHeight: '400px',
                            overflow: 'auto',
                        }}
                    >
                        {filteredApplications.length === 0 ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                style={{ padding: theme.spacing(3) }}
                            >
                                <Typography color="textSecondary">
                                    {search
                                        ? 'No applications match your search'
                                        : 'No applications available'}
                                </Typography>
                            </Box>
                        ) : (
                            <List>
                                {filteredApplications.map((application) => {
                                    const isSelected = selectedApplicationIds.has(
                                        application.id,
                                    )
                                    const isRequired = requiredApplicationIds.has(
                                        application.id,
                                    )
                                    const companyApp = company?.applications?.find(
                                        (a) => a.id === application.id,
                                    )
                                    // Company.billable_unit_count = number of units for total price calculation
                                    const unitCount =
                                        company?.billable_unit_count ?? 0
                                    const { perUnit, total } = getPriceDisplay(
                                        application,
                                        companyApp,
                                        unitCount,
                                        company,
                                    )

                                    if (isRequired) {
                                        return (
                                            <ListItem
                                                key={application.id}
                                                style={{
                                                    opacity: 0.9,
                                                    alignItems: 'flex-start',
                                                }}
                                            >
                                                <ListItemIcon
                                                    style={{
                                                        marginTop: 4,
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={true}
                                                        disabled
                                                        edge="start"
                                                    />
                                                </ListItemIcon>
                                                <Box
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        marginRight: theme.spacing(
                                                            2,
                                                        ),
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={`${
                                                            application.name
                                                        } (required)${
                                                            application.short_description
                                                                ? ` - ${application.short_description}`
                                                                : ''
                                                        }`}
                                                        secondary={
                                                            application.description
                                                        }
                                                    />
                                                </Box>
                                                <Box
                                                    flexShrink={0}
                                                    style={{
                                                        marginTop: 2,
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    <PriceDisplay
                                                        total={total}
                                                        perUnit={perUnit}
                                                        theme={theme}
                                                    />
                                                </Box>
                                            </ListItem>
                                        )
                                    }
                                    return (
                                        <ListItem
                                            key={application.id}
                                            button
                                            onClick={() =>
                                                handleToggleApplication(
                                                    application.id,
                                                )
                                            }
                                            style={{
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <ListItemIcon
                                                style={{
                                                    marginTop: 4,
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    edge="start"
                                                />
                                            </ListItemIcon>
                                            <Box
                                                style={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    marginRight: theme.spacing(
                                                        2,
                                                    ),
                                                }}
                                            >
                                                <ListItemText
                                                    primary={`${
                                                        application.name
                                                    }${
                                                        application.short_description
                                                            ? ` - ${application.short_description}`
                                                            : ''
                                                    }`}
                                                    secondary={
                                                        application.description
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                flexShrink={0}
                                                style={{
                                                    marginTop: 2,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                <PriceDisplay
                                                    total={total}
                                                    perUnit={perUnit}
                                                    theme={theme}
                                                />
                                            </Box>
                                        </ListItem>
                                    )
                                })}
                            </List>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                style={{
                    padding: theme.spacing(2, 3),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Box style={{ flex: 1 }}>
                    {!fetchingApplications && company && (
                        <Box>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginBottom: theme.spacing(0.25) }}
                            >
                                Selected applications total
                            </Typography>
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 700 }}
                            >
                                {formatTotalPrice(selectedTotalMonthly)}
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Box style={{ display: 'flex', gap: theme.spacing(1) }}>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={loading || fetchingApplications}
                        startIcon={
                            loading ? <CircularProgress size={16} /> : null
                        }
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
